import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens, typographyStyles, Label, Link } from "@fluentui/react-components";
import { ArrowDownload16Regular, Attach16Regular } from "@fluentui/react-icons";
import Dropzone, { DropEvent } from "react-dropzone";
import { AttachmentItem } from "../../../models/shared/AttachmentList";
import { Logger } from "../../../services/Logger";
import {
    filterExcludedFiles,
    convertFilePathToCorrectFormat,
    isTooManyFiles,
    hasFileExtension,
    isAttachmentDuplicated,
    isFolderAdded,
    buildExistingPaths,
    buildProjectAttachmentPaths,
    getMissingAttachments,
} from "./AttachmentsHelpers";
import { NewformaMessageBar } from "../../../components/_base/MessageBar";

export interface LocalFilesSectionProps extends LocalizeContextProps {
    attachments: AttachmentItem[];
    otherPageAttachments?: AttachmentItem[];
    /** Attachments from other sections sharing the panel (e.g. project files). Counted
     *  against the file limit and checked for name/path clashes on drop (rule D6). */
    additionalAttachments?: AttachmentItem[];
    logger: Logger;
    disabled: boolean;
    onAttachmentsUpdated: (attachments: AttachmentItem[]) => void;
    isCloudProject?: boolean;
    isFilePathInAttachmentsSupported?: boolean;
    isNL1221Supported?: boolean;
    classNameForScroll?: string;
    updateMoreOptions?: () => void;
    expandMoreOptions?: () => void;
    isIncludeReviewerAttachment?: boolean;
    customErrors?: {
        duplicateFile?: string;
        maximumFiles?: string;
    };
    shouldDismissErrors?: boolean;
    onDismissErrorsHandled?: () => void;
    showDropzone?: boolean;
}

const useStyles = makeStyles({
    dropzone: {
        border: `1px dashed ${tokens.colorNeutralStencil1}`,
        borderRadius: "4px",
        padding: "16px",
        textAlign: "center",
        cursor: "pointer",
        marginTop: "4px",
        marginBottom: tokens.spacingVerticalL,
        backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    dropzoneHint: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground2,
        textAlign: "center",
        margin: "0",
    },
    hintBold: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorBrandStroke1,
        cursor: "pointer",
    },
    hintIcon: {
        display: "inline",
        verticalAlign: "middle",
        marginLeft: "2px",
        marginRight: "2px",
        color: tokens.colorBrandStroke1,
    },

    messageBarTitle: {
        ...typographyStyles.caption1Strong,
        color: tokens.colorNeutralForeground1,
        display: "block",
    },
    messageBarInfo: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground1,
        display: "block",
    },
    localFilesHeader: {
        ...typographyStyles.body1,
        color: tokens.colorNeutralForeground1,
        marginTop: tokens.spacingVerticalL,
        marginBottom: "4px",
    },
    includeReviewerLink: {
        ...typographyStyles.caption1,
        display: "flex",
        alignItems: "center",
        gap: "4px",
        marginTop: "4px",
        marginBottom: "4px",
        cursor: "pointer",
    },
});

const LocalFilesSectionInner = (props: LocalFilesSectionProps) => {
    const styles = useStyles();

    const [uploadError, setUploadError] = useState<{ message: string; info?: string } | null>(null);
    const [blockedError, setBlockedError] = useState<{ message: string; info?: string } | null>(null);

    const prevUploadError = useRef<{ message: string; info?: string } | null>(null);
    const prevBlockedError = useRef<{ message: string; info?: string } | null>(null);

    // Scroll to bottom on new errors
    useEffect(() => {
        const hasNewError =
            (blockedError && prevBlockedError.current?.message !== blockedError.message) ||
            (uploadError && prevUploadError.current?.message !== uploadError.message);

        prevUploadError.current = uploadError;
        prevBlockedError.current = blockedError;

        if (hasNewError && props.classNameForScroll) {
            if (props.updateMoreOptions) {
                props.updateMoreOptions();
            }
            const el = document.querySelector(props.classNameForScroll);
            el?.scrollTo({ top: el.scrollHeight, behavior: "auto" });
        }
    }, [uploadError, blockedError]);

    // Dismiss errors when parent requests
    useEffect(() => {
        if (props.shouldDismissErrors) {
            onMessageDismissed();
            props.onDismissErrorsHandled?.();
        }
    }, [props.shouldDismissErrors]);

    const onMessageDismissed = useCallback(() => {
        setUploadError(null);
        setBlockedError(null);
    }, []);

    // Clear stale errors when the attachment list is emptied (e.g. "Clear" in the attachments list)
    useEffect(() => {
        if (props.attachments.length === 0) {
            onMessageDismissed();
        }
    }, [props.attachments.length]);

    const onDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
        if (acceptedFiles.length > 0 && fileRejections.length === 0) {
            setUploadError(null);
            setBlockedError(null);
        }
    }, []);

    const onDropAccepted = useCallback(
        (files: (File & { path?: string })[], _event: DropEvent) => {
            if (props.expandMoreOptions) {
                props.expandMoreOptions();
            }

            if (!files.length) {
                return;
            }

            try {
                const filesWithConvertedPaths = files.map((file) => ({
                    file,
                    path: convertFilePathToCorrectFormat(file.path ?? file.name),
                }));

                const existingPaths = [
                    ...buildExistingPaths(props.attachments, props.otherPageAttachments),
                    ...buildProjectAttachmentPaths(props.additionalAttachments),
                ];

                if (!props.isFilePathInAttachmentsSupported && isFolderAdded(filesWithConvertedPaths)) {
                    setUploadError({
                        message: props.translate(
                            "SHARED.ATTACHMENTS.ERROR_MESSAGE_FOR_FOLDERS_AND_OLD_NL_FLUENT9"
                        ) as string,
                        info: props.translate(
                            "SHARED.ATTACHMENTS.ERROR_MESSAGE_FOR_FOLDERS_AND_OLD_NL_FLUENT9_INFO"
                        ) as string,
                    });
                    return;
                }

                if (isAttachmentDuplicated(filesWithConvertedPaths, existingPaths)) {
                    setUploadError({
                        message:
                            props.customErrors?.duplicateFile ||
                            String(props.translate("SHARED.ATTACHMENTS.DUPLICATE_NAME_FLUENT9")),
                        info: props.customErrors?.duplicateFile
                            ? undefined
                            : (props.translate("SHARED.ATTACHMENTS.DUPLICATE_NAME_FLUENT9_INFO") as string),
                    });
                } else {
                    const validFiles = filterExcludedFiles(filesWithConvertedPaths);
                    const filesWithoutExtensions = validFiles.filter((file) => !hasFileExtension(file.file.name));

                    if (filesWithoutExtensions.length > 0) {
                        setBlockedError({
                            message: props.translate("SHARED.ATTACHMENTS.BLOCKED_FILE_TYPE_FLUENT9") as string,
                            info: props.translate("SHARED.ATTACHMENTS.BLOCKED_FILE_TYPE_FLUENT9_INFO") as string,
                        });
                        return;
                    }

                    const updatingFiles = validFiles.map((item) =>
                        props.isNL1221Supported
                            ? {
                                  file: item.file,
                                  id: item.path,
                                  name: item.file.name,
                                  lastModifiedDate: new Date(item.file.lastModified),
                              }
                            : {
                                  file: item.file,
                                  id: item.path,
                                  name: item.file.name,
                              }
                    );

                    const currentAttachmentCount =
                        props.attachments.length +
                        (props.otherPageAttachments?.length ?? 0) +
                        (props.additionalAttachments?.length ?? 0);
                    const nextAttachmentCount = currentAttachmentCount + updatingFiles.length;

                    if (isTooManyFiles(nextAttachmentCount)) {
                        setUploadError({
                            message:
                                props.customErrors?.maximumFiles ||
                                String(props.translate("SHARED.ATTACHMENTS.MAXIMUM_FILES_FLUENT9")),
                            info: props.customErrors?.maximumFiles
                                ? undefined
                                : (props.translate("SHARED.ATTACHMENTS.MAXIMUM_FILES_INFO_FLUENT9") as string),
                        });
                    } else {
                        props.onAttachmentsUpdated(props.attachments.concat(updatingFiles));
                    }
                }
            } catch {
                setUploadError({ message: props.translate("SHARED.ATTACHMENTS.ERROR") as string });
            }
        },
        [
            props.attachments,
            props.otherPageAttachments,
            props.additionalAttachments,
            props.isFilePathInAttachmentsSupported,
            props.isNL1221Supported,
        ]
    );

    const onDropRejected = useCallback(() => {
        setBlockedError({
            message: props.translate("SHARED.ATTACHMENTS.BLOCKED_FILE_TYPE_FLUENT9") as string,
            info: props.translate("SHARED.ATTACHMENTS.BLOCKED_FILE_TYPE_FLUENT9_INFO") as string,
        });
    }, [props.translate]);

    const includeReviewerAttachments = useCallback(() => {
        const missingItems = getMissingAttachments(props.attachments, props.otherPageAttachments);
        const combined = [...props.attachments, ...missingItems];
        if (isTooManyFiles(combined.length)) {
            setUploadError({
                message:
                    props.customErrors?.maximumFiles ||
                    String(props.translate("SHARED.ATTACHMENTS.MAXIMUM_FILES_FLUENT9")),
                info: props.customErrors?.maximumFiles
                    ? undefined
                    : (props.translate("SHARED.ATTACHMENTS.MAXIMUM_FILES_INFO_FLUENT9") as string),
            });
            return;
        }
        if (missingItems.length > 0) {
            props.onAttachmentsUpdated(combined);
            onMessageDismissed();
        }
    }, [props.attachments, props.otherPageAttachments, props.onAttachmentsUpdated]);

    const missingReviewerCount = useMemo(
        () => getMissingAttachments(props.attachments, props.otherPageAttachments).length,
        [props.attachments, props.otherPageAttachments]
    );

    const renderErrorBar = (error: { message: string; info?: string } | null, onDismiss: () => void) => {
        if (!error) return null;

        return (
            <NewformaMessageBar
                intent="error"
                body={
                    <>
                        <span className={styles.messageBarTitle}>{error.message}</span>
                        {error.info ? <span className={styles.messageBarInfo}>{error.info}</span> : null}
                    </>
                }
                onDismiss={onDismiss}
            />
        );
    };

    return (
        <>
            <Label className={styles.localFilesHeader}>
                {props.translate("SHARED.ATTACHMENTS.LOCAL_FILES") as string}
            </Label>

            {props.showDropzone !== false || props.attachments.length === 0 ? (
                <Dropzone
                    onDrop={onDrop}
                    onDropAccepted={onDropAccepted}
                    onDropRejected={onDropRejected}
                    multiple={true}
                    noClick={false}
                    noDrag={false}
                    noKeyboard={true}
                    minSize={1}
                    preventDropOnDocument={true}
                    disabled={props.disabled}
                >
                    {({ getRootProps, getInputProps }) => (
                        <div {...getRootProps({ className: styles.dropzone })} data-testid="local-files-dropzone">
                            <input {...getInputProps()} data-testid="local-files-dropzone-input" />
                            <p className={styles.dropzoneHint}>
                                <span className={styles.hintBold}>
                                    {props.translate("SHARED.ATTACHMENTS.DRAG_AND_DROP_HINT_1") as string}
                                </span>
                                <span>{props.translate("SHARED.ATTACHMENTS.DRAG_AND_DROP_HINT_2") as string}</span>
                                <span className={styles.hintIcon}>
                                    <ArrowDownload16Regular />
                                </span>
                                <span>
                                    {props.isFilePathInAttachmentsSupported
                                        ? (props.translate("SHARED.ATTACHMENTS.DRAG_AND_DROP_HINT_3") as string)
                                        : (props.translate(
                                              "SHARED.ATTACHMENTS.DRAG_AND_DROP_HINT_3_OLDER_NL"
                                          ) as string)}
                                </span>
                            </p>
                        </div>
                    )}
                </Dropzone>
            ) : null}

            {renderErrorBar(uploadError, () => setUploadError(null))}
            {renderErrorBar(blockedError, () => setBlockedError(null))}

            {props.isIncludeReviewerAttachment && !!props.otherPageAttachments?.length && missingReviewerCount > 0 && (
                <Link className={styles.includeReviewerLink} onClick={includeReviewerAttachments}>
                    <Attach16Regular />
                    {missingReviewerCount > 1
                        ? `${props.translate(
                              "SHARED.ATTACHMENTS.INCLUDE_REVIEWER_ATTACHMENTS"
                          )} (${missingReviewerCount})`
                        : `${props.translate(
                              "SHARED.ATTACHMENTS.INCLUDE_REVIEWER_ATTACHMENT"
                          )} (${missingReviewerCount})`}
                </Link>
            )}
        </>
    );
};

export default withLocalize(LocalFilesSectionInner);
